import { Link } from "react-router-dom";
import { Instagram, Linkedin, Mail } from "lucide-react";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { CONTACT, SERVICES, DISCLAIMER } from "@/data/content";
import NewsletterForm from "@/components/NewsletterForm";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/resources", label: "Resources" },
  { to: "/blog", label: "Blog" },
  { to: "/faq", label: "FAQ" },
  { to: "/book", label: "Book Now" },
  { to: "/contact", label: "Contact" },
];

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="border-t border-border bg-card">
      <div className="container-site border-b border-border py-10">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="font-heading text-lg font-semibold text-foreground">Practical tips, straight to your inbox</h3>
            <p className="mt-1 text-sm text-muted-foreground">One thoughtful email at a time — strategies, resources, and updates for caregivers.</p>
          </div>
          <NewsletterForm />
        </div>
      </div>
      <div className="container-site grid gap-12 py-16 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <Link to="/" aria-label="Evol & Troppus home"><Logo /></Link>
          <p className="mt-5 max-w-xs text-sm leading-relaxed text-muted-foreground">
            Family support built around people. Helping caregivers build confidence,
            connection, and consistency in everyday life.
          </p>
          <div className="mt-6 flex items-center gap-3">
            <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram-link"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-border transition-colors duration-300 hover:border-terracotta-500 hover:text-terracotta-500">
              <Instagram className="h-4 w-4" />
            </a>
            <a href={CONTACT.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid="footer-linkedin-link"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-border transition-colors duration-300 hover:border-terracotta-500 hover:text-terracotta-500">
              <Linkedin className="h-4 w-4" />
            </a>
            <a href={`mailto:${CONTACT.email}`} aria-label="Email" data-testid="footer-email-link"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-border transition-colors duration-300 hover:border-terracotta-500 hover:text-terracotta-500">
              <Mail className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div>
          <h3 className="overline-label">Navigate</h3>
          <ul className="mt-5 space-y-3">
            {NAV.map((l) => (
              <li key={l.to}>
                <Link to={l.to} data-testid={`footer-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="text-sm text-muted-foreground transition-colors duration-300 hover:text-terracotta-500">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="overline-label">Services</h3>
          <ul className="mt-5 space-y-3">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link to={s.href || `/services/${s.slug}`} data-testid={`footer-service-${s.slug}`}
                  className="text-sm text-muted-foreground transition-colors duration-300 hover:text-terracotta-500">
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="overline-label">Get in touch</h3>
          <ul className="mt-5 space-y-3 text-sm text-muted-foreground">
            <li><a href={`mailto:${CONTACT.email}`} data-testid="footer-contact-email" className="transition-colors duration-300 hover:text-terracotta-500">{CONTACT.email}</a></li>
            <li><a href={`tel:${CONTACT.phoneRaw}`} data-testid="footer-contact-phone" className="transition-colors duration-300 hover:text-terracotta-500">{CONTACT.phone}</a></li>
            <li className="pt-2">
              <Link to="/privacy" data-testid="footer-privacy-link" className="transition-colors duration-300 hover:text-terracotta-500">
                Privacy Policy
              </Link>
            </li>
          </ul>
          <div className="mt-6 flex items-center gap-3">
            <span className="text-xs text-muted-foreground">Theme</span>
            <ThemeToggle />
          </div>
        </div>
      </div>
      <div className="border-t border-border">
        <div className="container-site py-6">
          <p data-testid="footer-disclaimer" className="max-w-4xl text-xs leading-relaxed text-muted-foreground">
            {DISCLAIMER}
          </p>
        </div>
      </div>
      <div className="border-t border-border">
        <div className="container-site flex flex-col items-start justify-between gap-3 py-6 text-xs text-muted-foreground sm:flex-row sm:items-center">
          <p>© {new Date().getFullYear()} Evol &amp; Troppus. All rights reserved.</p>
          <p>Evidence-informed. Trauma-informed. Neurodiversity-affirming.</p>
        </div>
      </div>
    </footer>
  );
}
